import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Row, Space } from 'antd';
import {
  PlusOutlined,
  LockOutlined,
  SearchOutlined,
  SelectOutlined,
  SettingOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { v4 as uuid } from 'uuid';

import { PatternCodeType } from 'components/Admin/PatternCode';
import { Label, SecondaryButton, SuccessButton, message } from 'components/common';
import {
  GreenColor,
  SmartBackButton,
  SmartBlock,
  SmartButton,
  SmartFormLabel,
  SmartNumberInput,
  SmartScanner,
  SmartScannerInput,
  SmartSpace,
  //SmartSpin,
  playSound,
} from 'components/Sales/Smart/SmartComponent';
import { COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION, PoRecvStorageKey } from 'constants/config';
import { receive } from 'services/purchaseOrder';
import { fetchWarehouseLocationList } from 'services/warehouse';
import { getStorage } from 'utils';
import CodeInputDialog from './CodeInputDialog';
import CreateLotDrawer from './CreateLotDrawer';
import RecvConfirmDrawer from './RecvConfirmDrawer';
import RecvItemList from './RecvItemList';
import ScanQtySetting from './ScanQtySetting';
import SearchLotDrawer from './SearchLotDrawer';
import UomSelector, { checkoutUomBySkuAndCode } from './UomSelector';
import { DefaultScanRecvRatio, PoRecvActionType as PRAction, PoRecvStep, ScanRecvCountType, ScanRecvLotPolicy, ScanRecvRatioType, defaultUom } from './context';
import {
  clearMatchedPatternList,
  extractPatternCodeList,
  findMatchProductCode,
  getDefaultLocation,
  getItemReceivingMaxQty,
  getMatchItemReceivingQty,
  getMatchedPatternCode,
  isCodeMatchLine,
  isLotAvailableOnItem,
  isPatternCodeEnabled,
  setDefaultLocation,
} from '.';

enum RecvStep {
  ScanLocation,
  ScanItem,
  ReceivedItem,
};

type Props = ReduxStateProps & {
  gotoAnchor?: Function;
};

const RecvPo = (props: Props) => {
  const { useState } = React;
  const [currentConfigVersion, setCurrentConfigVersion] = useState(0);
  const [currentItem, setCurrentItem] = useState<StringKAnyVPair>({});
  const [currentItemCode, setCurrentItemCode] = useState('');
  const [currentItemLocked, setCurrentItemLocked] = useState(true);
  const [currentItemRecvQty, setCurrentItemRecvQty] = useState<number>();
  //const [currentLocation, setCurrentLocation] = useState<StringKAnyVPair>({});
  const [currentLotCode, setCurrentLotCode] = useState('');
  const [currentLotLocked, setCurrentLotLocked] = useState(true);
  const [currentPatternLocationVersion, setCurrentPatternLocationVersion] = useState(0);
  const [currentStep, setCurrentStep] = useState<RecvStep>(RecvStep.ScanLocation);
  // eslint-disable-next-line
  const [focusOnLocation, setFocusOnLocation] = useState(1);
  const [inited, setInited] = useState(false);
  const [isCreateLotDrawerOpen, setIsCreateLotDrawerOpen] = useState(false);
  const [isLocationDialogVisible, setIsLocationDialogVisible] = useState(false);
  const [isRecvConfirmDrawerOpen, setIsRecvConfirmDrawerOpen] = useState(false);
  const [isSearchLotDrawerOpen, setIsSearchLotDrawerOpen] = useState(false);
  const [itemRecvInputVersion, setItemRecvInputVersion] = useState(0);
  const [itemScannerCode, setItemScannerCode] = useState('');
  const [itemScannerVersion, setItemScannerVersion] = useState(0);
  const [lastConfigVersion, setLastConfigVersion] = useState(0);
  const [lastPatternLocationVersion, setLastPatternLocationVersion] = useState(0);
  const [lastRecvDirectlyVersion, setLastRecvDirectlyVersion] = useState(0);
  const [lastScanRecvVersion, setLastScanRecvVersion] = useState(0);
  const [lotScannerVersion, setLotScannerVersion] = useState(0);
  const [recvDirectlyVersion, setRecvDirectlyVersion] = useState(0);
  //const [recvItems, setRecvItems] = useState<StringKAnyVPair[]>([]);
  const [scanRecvVersion, setScanRecvVersion] = useState(0);
  const [uomSelectorVisible, setUomSelectorVisible] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();

  /*const recvCols = [
    {
      title: 'SKU',
      dataIndex: 'sku',
    },
    {
      title: 'UPC',
      dataIndex: 'upc',
    },
    {
      title: 'Location',
      dataIndex: 'receivingLocationCode',
    },
    {
      title: 'Receive Qty',
      dataIndex: 'receiveQty',
    },
  ];*/

  // eslint-disable-next-line
  const checkConfig = () => {
    const storage = getStorage('session');
    const scanRecvCountType = storage.getItem(PoRecvStorageKey.ScanRecvCountType);

    if (scanRecvCountType === null) {
      //openSetScanQtyDialog();
    }

    setDefaultItemRecvQty();
  };

  // eslint-disable-next-line
  const checkInitialRecvStep = () => {
    const { state } = props;
    const pct = PatternCodeType.Location;

    if (isPatternCodeEnabled(state)) {
      switch(pct) {
        case PatternCodeType.Location:
          const location = getMatchedPatternCode(state.matchedPatternList, pct);
          //console.log('l', location);
          if (location) handleLocationChange(location);
          else {
            // does there need to check the pattern sku
            if (isCurrentLocationSet() && state.recvItems.length > 0) setCurrentStep(RecvStep.ScanItem);
            else {
              state.defaultLocation && handleLocationChange(state.defaultLocation);
            }
          }
          break;
      }
    } else {
      state.defaultLocation && handleLocationChange(state.defaultLocation);
    }
  };

  // eslint-disable-next-line
  const checkPatternCode = (
    pct: PatternCodeType,
    item: StringKAnyVPair = currentItem,
  ) => {
    const { dispatch, state } = props;

    if (isPatternCodeEnabled(state)) {
      switch(pct) {
        case PatternCodeType.LOT:
          if (isLotAvailableOnItem(state, item)) {
            const lot = getMatchedPatternCode(state.matchedPatternList, pct);

            if (lot) {
              setCurrentLotCode(lot);
              setLotScannerVersion(lotScannerVersion + 1);
            }
          }

          break;

        case PatternCodeType.Location:
          const location = getMatchedPatternCode(state.matchedPatternList, pct);
          //console.log('l', location);
          if (location) handleLocationChange(location);
          break;

        case PatternCodeType.Qty:
          const qty = getMatchedPatternCode(state.matchedPatternList, pct);

          //console.log('qty', qty, typeof qty);
          if (Number(qty) > 0) {
            setCurrentItemRecvQty(Number(qty));
            setItemRecvInputVersion(itemRecvInputVersion + 2);
            currentItemLocked && setCurrentItemLocked(false);
          }

          break;

        case PatternCodeType.SKU:
          const sku = getMatchedPatternCode(state.matchedPatternList, pct);
          //console.log('s', sku);
          setItemScannerCode(sku || '');

          if (sku) {
            onScanItem(sku);
          } else {
            setItemScannerVersion(itemScannerVersion + 1);
          }

          break;

        case PatternCodeType.UOM:
          if (state.enableUom) {
            const uomCode = getMatchedPatternCode(state.matchedPatternList, pct);

            //console.log('it', item, uomCode, state);
            if (uomCode) checkoutUomBySkuAndCode(item.sku, uomCode, state, dispatch);
          }

          break;
      }
    }
  };

  const closeConfirmReceivingItemsDrawer = () => {
    setIsRecvConfirmDrawerOpen(false);
  };

  const closeCreateLotDrawer = () => {
    setIsCreateLotDrawerOpen(false);
  };

  const closeScanLocationDialog = () => {
    setIsLocationDialogVisible(false);
  };

  const closeSearchLotDrawer = () => {
    setIsSearchLotDrawerOpen(false);
  };

  const closeUomSelector = () => {
    setUomSelectorVisible(false);
  };

  const confirmItemsButton = () => {
    return (
      <SmartButton
        className="mb-0 mt-10"
        disabled={!isReceivingItemListSavable()}
        //onClick={saveReceivingItems}
        onClick={openConfirmReceivingItemsDrawer}
        size="large"
        type="primary"
      >
        Confirm
      </SmartButton>
    );
  };

  const currentItemLockClass = () => {
    const cls = ['fs-20', 'h-50', 'w-48', 'p-4-12'];

    if (currentItemLocked) {
      cls.push('default-btn-danger');
    }

    return cls.join(' ');
  };

  const currentLotLockClass = () => {
    const cls = ['fs-20', 'h-50', 'w-48', 'p-4-12'];

    if (currentLotLocked) {
      cls.push('default-btn-danger');
    }

    return cls.join(' ');
  };

  const findMatchItem = (
    itemCode: string,
    locationCode: string,
    lotCode = currentLotCode,
  ) => {
    const { state } = props;

    props.state.recvItems.forEach((e: any) => console.log(isLotAvailableOnItem(props.state, e), e._receivingLot === lotCode, lotCode));
    //return state.recvItems.find((e: StringKAnyVPair) => e.receivingLocationCode === locationCode && isCodeMatchLine(itemCode, e) && (isLotAvailableOnItem(state, e) ? e._receivingLot === lotCode : true) && (state.enableUom ? (e.uom === state.uomInfo.uom && e.uomRate === state.uomInfo.uomRate) : true));
    return state.recvItems.find((e: StringKAnyVPair) => e.poNum === state.currentPo.poNum && e.receivingLocationCode === locationCode && isCodeMatchLine(itemCode, e) && (isLotAvailableOnItem(state, e) ? e._receivingLot === lotCode : true) && (state.enableUom ? isItemWithProductCodeUomOrStateUom(e, itemCode) : true));
  };

  const findMatchItem2 = (itemCode: string, locationCode: string) => {
    const { state } = props;

    return state.recvItems.find((e: StringKAnyVPair) => e.poNum === state.currentPo.poNum && e.receivingLocationCode === locationCode && isCodeMatchLine(itemCode, e));
  };

  const findMatchItemOrLine = (itemCode: string, locationCode: string, lotCode = currentLotCode) => {
    return findMatchItem(itemCode, locationCode, lotCode) || findMatchLine(itemCode);
  };

  const findMatchLine = (code: string) => {
    return props.state.poLines.find((l: StringKAnyVPair) => isCodeMatchLine(code, l));
  };

  const getDefaultItemRecvQty = (item: StringKAnyVPair) => {
    const { state } = props;
    let qty = (currentItemRecvQty || 1) * DefaultScanRecvRatio;

    if (state.scanRecvCountType === ScanRecvCountType.Custom) {
      if (state.scanRecvRatioType === ScanRecvRatioType.FixedRatio) {
        qty = 1 * state.scanRecvRatio;
      } else if (state.scanRecvRatioType === ScanRecvRatioType.Unreceived) {
        qty = getItemReceivingMaxQty(state.recvItems, item) - (item._receivingQty || 0);
      }
    }

    return qty;
  };

  const getLatestFirstRecvItems = (
    items: StringKAnyVPair[],
    latestUuid: string,
  ) => {
    const idx = items.findIndex(e => e._recvUuid === latestUuid);
    const itemList = [ ...items ];
    let latest: StringKAnyVPair[] = [];

    if (idx > -1) latest = itemList.splice(idx, 1);

    return [ ...latest, ...itemList ];
  };

  const getMatchItemByCode = (
    itemCode: string,
    locationCode: string,
    lotCode = currentLotCode,
    uomInfo = props.state.uomInfo,
  ) => {
    const { state } = props;
    const item = findMatchItemOrLine(itemCode, locationCode, lotCode);

    if (item) {
      if ((isCurrentLocationSet() && !isItemInCurrentLocation(item)) || (state.enableUom ? !isItemWithUom(item, uomInfo) : false)) {
        const ret: StringKAnyVPair = { ...item };

        //console.log('pi', state.currentPo);
        ret._baseQty = 0;
        ret._fulfillmentPoId = state.currentPo.fulfillmentPoId;
        ret._poNumber = state.currentPo.poNumber;
        ret._recvUuid = uuid();
        ret._receivingLot = '';
        ret._receivingQty = 0;
        //ret.receivingLocationCode = currentLocation.locationCode;
        ret.receivingLocationCode = state.currentLocation.locationCode;
        //ret.receivingLocationNum = currentLocation.locationNum;
        ret.receivingLocationNum = state.currentLocation.locationNum;
        delete ret.receivingQty;

        return ret;
      }
    }

    return item;
  };

  const getRecvItemGroupList = (
    items: StringKAnyVPair[],
  ): StringKAnyVPair[][] => {
    const dict: StringKAnyVPair = {};

    items.forEach(e => {
      if (!dict[e.poNum]) dict[e.poNum] = [];

      dict[e.poNum].push(e);
    });

    return Object.values(dict);
  };

  const gotoPrev = () => {
    const { dispatch, state } = props;

    if (state.recvItems.length > 0) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Are you sure you want to go back? All data will be lost.',
        onOk() {
          dispatch({ type: PRAction.SetRecvItems, params: [] });
          isPatternCodeEnabled(state) && clearMatchedPatternList(dispatch);
          history.goBack();
        },
      });
    } else {
      isPatternCodeEnabled(state) && clearMatchedPatternList(dispatch);
      //history.goBack();
      props.gotoAnchor && props.gotoAnchor(PoRecvStep.ScanPo);
    }
  };

  const handleLocationChange = async (str: string) => {
    const { dispatch, state } = props;
    const val = str.trim();

    if (val === '') return;

    if (isPatternCodeEnabled(state)) {
      //const ln = getMatchedPatternCode(state.matchedPatternList || [], PatternCodeType.Location);
      const ln = getMatchedPatternCode(extractPatternCodeList(state, val) || [], PatternCodeType.Location);

      console.log('->', ln, val, state.currentLocation);
      if (ln && ln !== val) {
        if (tryNextStepByPatternCode(val)) return;
      }
    }

    playSound('bell');
    // location should be put into state in order to improve efficiency
    dispatch({ type: PRAction.SetLockScreen, params: true }); 
    try {
      //console.log('w', state.selectedWarehouse);
      const res = await fetchWarehouseLocationList(
        state.selectedWarehouse.warehouseId,
        false,
        val,
        0,
      );

      if (Array.isArray(res)) {
        if (res.length === 1) {
          console.log('location', res[0]);
          //setCurrentLocation(res[0]);
          dispatch({ type: PRAction.SetCurrentLocation, params: res[0] });
          setCurrentStep(RecvStep.ScanItem);

          if (isPatternCodeEnabled(state)) setCurrentPatternLocationVersion(currentPatternLocationVersion + 1);
        } else {
          playSound('error');
          if (res.length === 0) {
            message.error(`Not found location [${val}]`);
            if (isPatternCodeEnabled(state) && isCurrentLocationSet() && state.recvItems.length > 0) {
              setCurrentStep(RecvStep.ScanItem);
              onClearCurrentLocation();
            }
          } else {
            message.error(`Found ${res.length} location(s) for [${val}]`);
          }
        }
      } else {
        playSound('error');
        message.error(`Format error, not found location [${val}]`);
      }
    } catch(e) {
      playSound('error');
      message.error(`Fetched location information error: ${e}`);
    } finally {
      dispatch({ type: PRAction.SetLockScreen, params: false }); 
    }
  };

  const isCurrentLocationSet = () => {
    //return currentLocation.locationNum > 0;
    return props.state.currentLocation.locationNum > 0;
  };

  const isItemInCurrentLocation = (e: StringKAnyVPair) => {
    const { state } = props;

    return isCurrentLocationSet()
        //&& e.receivingLocationCode === currentLocation.locationCode
        //&& e.receivingLocationNum === currentLocation.locationNum;
        && e.receivingLocationCode === state.currentLocation.locationCode
        && e.receivingLocationNum === state.currentLocation.locationNum;
  };

  const isItemScannable = () => {
    return currentStep === RecvStep.ScanItem && isCurrentLocationSet();
  };

  const isItemScanned = () => {
    return currentItem.poLineNum > 0;
  };

  const isItemSubmittable = (
    checkItem = false,
    checkLotCode = false,
    checkQty = false,
  ) => {
    //let ret = currentStep === RecvStep.ScanItem && isItemScanned();
    //let ret = currentStep === RecvStep.ScanItem;
    let ret = isItemScannable();

    if (ret) {
      if (checkLotCode && isLotAvailableOnItem(props.state, currentItem)) ret = isLotScanned();
      if (ret && checkItem) ret = isItemScanned();
      if (ret && checkQty) ret = (currentItemRecvQty || 0) > 0;
    }

    return ret;
  };

  const isItemWithProductCodeUomOrStateUom = (
    item: StringKAnyVPair,
    itemCode: string,
  ) => {
    const { state } = props;
    const pcItem = findMatchProductCode(itemCode, item);

    return pcItem ? isItemWithUom(item, pcItem) : isItemWithUom(item, state.uomInfo);
  };

  const isItemWithUom = (item: StringKAnyVPair, uom: StringKAnyVPair) => {
    return item.uom === uom.uom && item.uomRate === uom.uomRate;
  };

  const isLocationButtonEnable = () => {
    //return currentStep === RecvStep.ScanItem && currentItemCode === '';
    return currentStep === RecvStep.ScanItem && isCurrentLocationSet();
  };

  const isLocationScannable = () => {
    return currentStep === RecvStep.ScanLocation || (currentStep === RecvStep.ScanItem && !isCurrentLocationSet());
  };

  const isLotReleased = () => {
    return isLotScannable(true) ? !currentLotLocked : false;
  };

  const isLotScanned = () => {
    return !!currentLotCode.trim();
  };

  const isLotScannable = (checkItem = false) => {
    //return currentStep === RecvStep.ScanItem && (checkItem ? (isItemScanned() && isLotAvailableOnItem(props.state, currentItem)) : props.state.enableLot);
    return isItemScannable() && (checkItem ? (isItemScanned() && isLotAvailableOnItem(props.state, currentItem)) : props.state.enableLot);
    //return currentStep === RecvStep.ScanItem && isLotAvailableOnItem(props.state, currentItem);
  };

  const isReceivingItemListSavable = () => {
    return props.state.recvItems.length > 0 && currentItemCode === '';
  };

  const isPageRolledBack = () => {
    //return props.state.recvItems.length === 0 && !isItemSubmittable();
    //return props.state.recvItems.length === 0;
    return true;
  };

  const isQtyChangeable = (checkItem = false) => {
    return props.state.scanRecvCountType === ScanRecvCountType.Default ? (checkItem ? (isItemScanned() && !currentItemLocked) : !currentItemLocked) : false;
  };

  const isSameCodeInCurrentItem = (code: string) => {
    return isItemScanned() && ([currentItem.sku, currentItem.upc].indexOf(code) > -1);
  };

  const isUomEnable = (checkItem = false) => {
    const { state } = props;

    //console.log('cc', currentItemCode, currentItem);
    //return currentStep === RecvStep.ScanItem && (checkItem ? (state.enableUom && isItemScanned() && !findMatchProductCode(currentItemCode, currentItem)) : state.enableUom);
    return isItemScannable() && (checkItem ? (state.enableUom && isItemScanned() && !findMatchProductCode(currentItemCode, currentItem)) : state.enableUom);
    //return currentStep === RecvStep.ScanItem && isLotAvailableOnItem(props.state, currentItem);
  };

  const onClearCurrentLocation = () => {
    props.dispatch({ type: PRAction.SetCurrentLocation, params: {} });
  };

  const onClearLotCode = () => {
    setCurrentLotCode('');
  };

  const onClearScannedItem = (checkLock = false) => {
    const { dispatch } = props;
    console.log('clear scanned item', checkLock);
    setCurrentItem({});
    setCurrentItemCode('');
    //setCurrentItemLocked(true);
    //setCurrentItemRecvQty(undefined);
    setDefaultItemRecvQty(checkLock);
    //if (!checkLock || (checkLock && !currentItemLocked)) setCurrentItemRecvQty(1);
    if (!checkLock || (checkLock && !currentLotLocked)) {
      setCurrentLotCode('');
      setLotScannerVersion(lotScannerVersion + 1);
    }

    setItemRecvInputVersion(itemRecvInputVersion + 1);
    setItemScannerCode('');
    setItemScannerVersion(itemScannerVersion + 1);
    dispatch({ type: PRAction.SetUomInfo, params: defaultUom });
  };

  const onItemScannerKeyDown = (evt: any) => {
    if ([9, 13].indexOf(evt.keyCode) > -1) {
      evt.preventDefault();
    }
  };

  const onScanContinuousItem = () => {
    setItemScannerCode('');
    setCurrentItemRecvQty((currentItemRecvQty || 0) + 1);
    setItemScannerVersion(itemScannerVersion + 1);
    setItemRecvInputVersion(itemRecvInputVersion + 1);
  };

  const onScanItem = (itemCode: string) => {
    const { state } = props;
    const code = itemCode.trim();
    const line = findMatchLine(code);

    if (!line && isPatternCodeEnabled(state)) {
      const mSku = getMatchedPatternCode(state.matchedPatternList || [], PatternCodeType.SKU);

      console.log('ms', mSku, state);
      if (mSku !== code) {
        if (tryNextStepByPatternCode(code)) return;
      }
    }

    if (!isCurrentLocationSet()) {
      playSound('error');
      message.error({ content: 'Please scan a right location at first', duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION });
      return;
    }

    if (code !== currentItemCode) {
      //console.log('item', line, code, ' - ', currentItemCode);
      if (isItemScanned() && (!isSameCodeInCurrentItem(code))) {
        playSound('error');
        message.error({ content: 'Please save current item before receiving a new item', duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION });
        setTimeout(() => {
          setItemScannerCode('');
          setItemScannerVersion(itemScannerVersion + 1);
        }, 500);
        return;
      }

      setCurrentItemCode(code);

      if (code !== itemCode) {
        setItemScannerCode(code);
      }

      if (line) {
        //const item = getMatchItemByCode(code, currentLocation.locationCode);
        const item = getMatchItemByCode(code, state.currentLocation.locationCode);
        console.log('l', line, item);
        playSound('bell');
        setItemRecvInputVersion(itemRecvInputVersion + 1);

        if (item) {
          const prdCode = findMatchProductCode(code, item);
          const rQty = getDefaultItemRecvQty(item);
          let recvDirectly = false;
          let lotRecvable = false;

          if (rQty === 0) {
            playSound('error');
            Modal.error({
              title: 'Error',
              content: `The required quantity of [${code}] has been received`,
              okButtonProps: {
                //size: 'large',
              },
              onOk() {
                setCurrentItemCode('');
                setItemScannerCode('');
                setItemScannerVersion(itemScannerVersion > 0 ? 0 : 1);
              },
            });
            return;
          }

          if (isLotAvailableOnItem(state, item)) {
            switch (state.scanRecvLotPolicy) {
              case ScanRecvLotPolicy.AssignSameLot:
                setCurrentLotCode(props.state.scanRecvLotUniNum);
                //setLotCodeOnFormItem(item, props.state.scanRecvLotUniNum);
                setLotScannerVersion(lotScannerVersion + 1);

                //if (currentLotLocked && currentItemLocked) submitRecvForm();
                if (currentLotLocked && currentItemLocked) lotRecvable = true;
              break;

              case ScanRecvLotPolicy.InputManually:
                //console.log('hl', currentLotCode);
                if (currentLotLocked) {
                  //if (isLotScanned()) submitRecvForm();
                  if (isLotScanned()) lotRecvable = true;
                  else setCurrentLotLocked(false);
                } else {
                  if (!currentLotLocked) {
                    setCurrentLotCode('');
                    setLotScannerVersion(lotScannerVersion + 1);
                  }
                }
              break;
            }
          } else {
            if (currentItemLocked) {
              if (state.enableUom) {
                if (prdCode) recvDirectly = true;
              } else {
                recvDirectly = true;
              }
            }
          }

          //console.log('si', item._recvUuid, item, prdCode, recvDirectly);
          setCurrentItem(item);
          setCurrentItemRecvQty(rQty);
          trySetFormByItemCode(itemCode, item);

          checkPatternCode(PatternCodeType.LOT, item);
          checkPatternCode(PatternCodeType.UOM, item);
          checkPatternCode(PatternCodeType.Qty, item);

          if (!recvDirectly) {
            if (state.scanRecvCountType === ScanRecvCountType.Custom && !isLotAvailableOnItem(state, item)) {
              if (state.enableUom) {
                if (prdCode) recvDirectly = true;
              } else {
                recvDirectly = true;
              }
            }

            if (isLotAvailableOnItem(state, item)) {
              //console.log('-&->', recvDirectly, lotRecvable);
              if (!recvDirectly && lotRecvable) {
                if (state.enableUom) {
                  recvDirectly = !!prdCode;
                } else {
                  recvDirectly = true;
                }
              }
            }
          }

          if (recvDirectly) setScanRecvVersion(scanRecvVersion + 1);
          else {
            (!isPatternCodeEnabled(state)) && onScanContinuousItem();
          }
        }
      } else {
        playSound('error');
        message.error({ content: `[${code}] not found`, duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION });
      }
    } else if (code !== itemScannerCode) {
      if (isPatternCodeEnabled(state)) setItemScannerCode(code)
      else {
        onScanContinuousItem();
        playSound('bell');
      }
    }
  };

  const onScanLot = (lotStr: string) => {
    const { state } = props;
    const lotCode = lotStr.trim();
    //const item = getMatchItemByCode(currentItemCode, currentLocation.locationCode, lotCode);
    const item = getMatchItemByCode(currentItemCode, state.currentLocation.locationCode, lotCode);

    //console.log('sl', item, item._recvUuid);
    if (item) {
      // note: the item is different from the item setted when scanning sku
      setCurrentItem(item);
      setCurrentItemRecvQty(getDefaultItemRecvQty(item));
      setLotCodeOnFormItem(item, lotCode);
      playSound('bell');

      //if (currentItemLocked) submitRecvForm();
    } else {
      playSound('error');
      message.error({ content: `LOT#[${lotCode}] not found` });
    }
  };

  const onSelectLot = (lot: StringKAnyVPair) => {
    //console.log('lot', lot);
    onScanLot(lot.lotNumber);
    setLotScannerVersion(lotScannerVersion + 1);
  };

  const onSelectUom = (uomInfo?: StringKAnyVPair) => {
    //const item = getMatchItemByCode(currentItemCode, currentLocation.locationCode, currentLotCode, uomInfo || props.state.uomInfo);
    const item = getMatchItemByCode(currentItemCode, props.state.currentLocation.locationCode, currentLotCode, uomInfo || props.state.uomInfo);

    if (item) {
      //console.log('su', uomInfo, item, item._recvUuid);
      setCurrentItem(item);
      playSound('bell');
    }
  };

  const onSwitchLocation = (lc: string, options: StringKAnyVPair[]) => {
    const { dispatch } = props;
    //setCurrentLocation({});
    //console.log('switch location', lc, options);
    handleLocationChange(lc);
    options[0] && options[0].checked && setDefaultLocation(dispatch, lc);
  };

  const openConfirmReceivingItemsDrawer = () => {
    setIsRecvConfirmDrawerOpen(true);
  };

  const openCreateLotDrawer = () => {
    setIsCreateLotDrawerOpen(true);
  };

  const openScanLocationDialog = () => {
    setIsLocationDialogVisible(true);
  };

  const openSearchLotDrawer = () => {
    setIsSearchLotDrawerOpen(true);
  };

  const openSetScanQtyDialog = () => {
    const { state, dispatch } = props;
    let scanRecvCountType = state.scanRecvCountType;
    let scanRecvLotPolicy = state.scanRecvLotPolicy;
    let scanRecvLotUniNum = state.scanRecvLotUniNum;
    let scanRecvRatio = state.scanRecvRatio;
    let scanRecvRatioType = state.scanRecvRatioType;

    const onChangeScanRecvCountType = (ct: ScanRecvCountType) => {
      scanRecvCountType = ct;
      //console.log('sv', ct);
    };

    const onChangeScanRecvLotPolicy = (lp: ScanRecvLotPolicy) => {
      scanRecvLotPolicy = lp;
    };

    const onChangeScanRecvLotUniNum = (ln: string) => {
      scanRecvLotUniNum = ln;
    };

    const onChangeScanRecvRatio = (ratio: number) => {
      scanRecvRatio = ratio;
    };

    const onChangeScanRecvRatioType = (rt: ScanRecvRatioType) => {
      scanRecvRatioType = rt;
    };

    Modal.confirm({
      title: 'Scan Qty Setting',
      bodyStyle: {padding: '12px 12px'},
      icon: null,
      content: (
        <ScanQtySetting
          dispatch={dispatch}
          onChangeScanRecvCountType={onChangeScanRecvCountType}
          onChangeScanRecvLotPolicy={onChangeScanRecvLotPolicy}
          onChangeScanRecvLotUniNum={onChangeScanRecvLotUniNum}
          onChangeScanRecvRatio={onChangeScanRecvRatio}
          onChangeScanRecvRatioType={onChangeScanRecvRatioType}
          state={state}
        />
      ),
      onOk() {
        const storage = getStorage('session');

        dispatch({ type: PRAction.SetScanRecvCountType, params: scanRecvCountType });
        storage.setItem(PoRecvStorageKey.ScanRecvCountType, scanRecvCountType);
        dispatch({ type: PRAction.SetScanRecvLotPolicy, params: scanRecvLotPolicy });
        storage.setItem(PoRecvStorageKey.ScanRecvLotPolicy, scanRecvLotPolicy);
        dispatch({ type: PRAction.SetScanRecvLotUniNum, params: scanRecvLotUniNum });
        storage.setItem(PoRecvStorageKey.ScanRecvLotUniNum, scanRecvLotUniNum);
        dispatch({ type: PRAction.SetScanRecvRatio, params: scanRecvRatio });
        storage.setItem(PoRecvStorageKey.ScanRecvRatio, scanRecvRatio);
        dispatch({ type: PRAction.SetScanRecvRatioType, params: scanRecvRatioType });
        storage.setItem(PoRecvStorageKey.ScanRecvRatioType, scanRecvRatioType);

        if (scanRecvLotPolicy === ScanRecvLotPolicy.AssignSameLot) {
          setCurrentLotCode(scanRecvLotUniNum);
          setLotScannerVersion(lotScannerVersion + 1);
        } else {
          if (scanRecvLotUniNum === currentLotCode) {
            setCurrentLotCode('');
            setLotScannerVersion(lotScannerVersion + 1);
          }
        }

        setCurrentConfigVersion(currentConfigVersion + 1);
        setTimeout(() => {
          // must use timeout here
          setItemRecvInputVersion(itemRecvInputVersion + 1);
        }, 0);
      },
    });
  };

  const openUomSelector = () => {
    console.log('u', currentItem);
    setUomSelectorVisible(true);
  };

  // eslint-disable-next-line
  const receiveItem = () => {
    const { dispatch, state } = props;
    //const item = findMatchItem(currentItemCode, currentLocation.locationCode);
    const item = findMatchItem(currentItemCode, state.currentLocation.locationCode);
    const line = findMatchLine(currentItemCode);
    const nQty = getMatchItemReceivingQty(state.recvItems, currentItemCode);
    const recvQty = currentItemRecvQty || 0;
    const baseQty = state.uomInfo.uomRate * recvQty;
    let recvItems: StringKAnyVPair[] = [];
    console.log('ur', recvQty, baseQty);
    // check whether the Qty is invaild
    if (state.enablePOReceiveControl) {
      const maxQty = Math.round(line.poQty * (1 + state.poOverRecvPercent));

      //if (nQty + recvQty + line.humanReceiveQty > maxQty) {
      if (nQty + baseQty + line.humanReceiveQty > maxQty) {
        playSound('error');
        Modal.error({
          title: 'Error',
          content: `Cannot exceed total quatity[${maxQty}]`,
          okButtonProps: {
            //size: 'large',
          },
        });
        return;
      }
    }

    if (item) {
      //console.log('item', item, currentItemRecvQty);
      //currentItem._receivingQty = currentItem._receivingQty + currentItemRecvQty;
      item._receivingQty = item._receivingQty + recvQty;
      item._baseQty = item._baseQty + baseQty;
      //setRecvItems([...recvItems]);
      recvItems = getLatestFirstRecvItems([...state.recvItems], item._recvUuid);
      //dispatch({ type: PRAction.SetRecvItems, params: getLatestFirstRecvItems([...state.recvItems], item._recvUuid) });
      dispatch({ type: PRAction.SetRecvItems, params: [ ...recvItems ] });
    } else {
      currentItem._baseQty = baseQty;
      currentItem._receivingLot = currentLotCode;
      currentItem._receivingQty = recvQty;
      currentItem.uom = state.uomInfo.uom;
      currentItem.uomRate = state.uomInfo.uomRate;
      //setRecvItems([currentItem, ...recvItems]);
      recvItems = [currentItem, ...state.recvItems];
      //dispatch({ type: PRAction.SetRecvItems, params: [currentItem, ...state.recvItems] });
      dispatch({ type: PRAction.SetRecvItems, params: [ ...recvItems ] });
    }
    console.log('r->', item, currentItem._recvUuid, currentItem, line);

    if (line) {
      const qty = getMatchItemReceivingQty(state.recvItems, currentItemCode);

      //console.log('rqty', qty);
      //line.receivingQty = qty + (currentItemRecvQty || 0);
      line.receivingQty = qty + recvQty;
      line.baseQty = qty + baseQty;
      dispatch({ type: PRAction.SetPoLines, params: [...state.poLines] });
    }

    playSound('success');
    onClearScannedItem(true);

    if (isPatternCodeEnabled(state)) {
      state.matchedPatternList.length > 0 && clearMatchedPatternList(dispatch);
    }
    // save the item directly
    saveReceivingItems(recvItems);
  };

  const receiveNextItemInAnotherLocation = () => {
    const { dispatch } = props;

    onClearScannedItem();
    //setCurrentLocation({});
    //setRecvItems([]);
    dispatch({ type: PRAction.SetRecvItems, params: [] });
    setCurrentStep(RecvStep.ScanLocation);
  };

  const receiveNextItemInCurrentLocation = () => {
    const { dispatch } = props;

    onClearScannedItem();
    //setRecvItems([]);
    dispatch({ type: PRAction.SetRecvItems, params: [] });
    setCurrentStep(RecvStep.ScanItem);
  };

  const receiveNextPO = () => {
    if (props.gotoAnchor) {
      props.dispatch({ type: PRAction.SetPoLines, params: [] });
      props.dispatch({ type: PRAction.SetPoList, params: [] });
      props.dispatch({ type: PRAction.SetRecvItems, params: [] });
      props.gotoAnchor(PoRecvStep.ScanPo);
    }
  };

  const receiveNextPOByPatternCode = (pc: string) => {
    if (props.gotoAnchor) {
      //props.dispatch({ type: PRAction.SetPoLines, params: [] });
      props.dispatch({ type: PRAction.SetPoList, params: [] });
      props.dispatch({ type: PRAction.SetNextPatternCode, params: pc });
      props.gotoAnchor(PoRecvStep.ScanPo);
    }
  };

  const receiveNextStepWizard = () => {
    return (<>
      <SmartButton
        className="mb-0 mt-10"
        //disabled={!isReceivingItemListSavable()}
        onClick={receiveNextItemInCurrentLocation}
        size="large"
        type="primary"
      >
        Continue in Current Location
      </SmartButton>
      <SmartButton
        className="mb-0 mt-10"
        //disabled={!isReceivingItemListSavable()}
        onClick={receiveNextItemInAnotherLocation}
        size="large"
        type="primary"
      >
        Receive to Another Location
      </SmartButton>
      {props.gotoAnchor && !isPatternCodeEnabled(props.state) && <SmartButton
        className="mb-0 mt-10"
        //disabled={!isReceivingItemListSavable()}
        onClick={receiveNextPO}
        size="large"
        type="primary"
      >
        Receive Another PO
      </SmartButton>}
    </>);
  };

  const saveReceivingItems = async (
    recvItems: StringKAnyVPair[] = props.state.recvItems,
  ) => {
    const { dispatch, state } = props;
    //const recvItemGroups = getRecvItemGroupList(state.recvItems);
    const recvItemGroups = getRecvItemGroupList(recvItems);
    let saved = false;

    console.log(state);
    for (let i = 0; i < recvItemGroups.length; i++) {
      const g = recvItemGroups[i];

      dispatch({ type: PRAction.SetLockScreen, params: true }); 
      try {
        //const res = await receive(state.currentPo.fulfillmentPoId, {
        const res = await receive(g[0].poId, {
          WarehouseNum: state.selectedWarehouse.warehouseNum,
          //Items: state.recvItems.map((e: StringKAnyVPair) => ({
          // eslint-disable-next-line
          Items: g.map((e: StringKAnyVPair) => ({
            SKU: e.sku,
            FulfillmentPoLineNum: e.fulfillmentPoLineNum,
            //ReceiveQty: e.humanReceiveQty + e._receivingQty,
            ReceiveQty: e._receivingQty,
            LocationNum: e.receivingLocationNum,
            LotNumber: isLotAvailableOnItem(state, e) ? e._receivingLot : undefined,
            //ReceiptDate: moment(receiptDate).format('YYYY-MM-DD'),
            ReceiptDate: moment().format('YYYY-MM-DD'),
            Uom: e.uom,
            UomRate: e.uomRate,
          })),
        });

        if (res) {
          //setCurrentStep(RecvStep.ReceivedItem);
          saved = true;
          console.log('s', g[0].poId);
        }
      } catch(e) {
        message.error(`Received error: ${e}`);
      } finally {
        dispatch({ type: PRAction.SetLockScreen, params: false }); 
      }
    }

    if (saved) {
      if (isPatternCodeEnabled(state)) dispatch({ type: PRAction.SetMatchedPatternList, params: [] });

      if (isPatternCodeEnabled(state)) {
        receiveNextPO();
      } else {
        setCurrentStep(RecvStep.ReceivedItem);
      }
    }
  };

  // eslint-disable-next-line
  const setDefaultItemRecvQty = (checkLock = false) => {
    const { state } = props;
    let qty: any = currentItemRecvQty;

    if (state.scanRecvCountType === ScanRecvCountType.Custom) {
      if (state.scanRecvRatioType === ScanRecvRatioType.FixedRatio) {
        qty = 1 * state.scanRecvRatio;
      } else if (state.scanRecvRatioType === ScanRecvRatioType.Unreceived) {
        //qty = getItemReceivingMaxQty(state.recvItems, item) - (item._receivingQty || 0);
        qty = undefined;
      }
    } else {
      if (!checkLock || (checkLock && !currentItemLocked)) {
        qty = isPatternCodeEnabled(state) ? 1 * DefaultScanRecvRatio : undefined;
      }
    }
    //console.log(' -> ', state.scanRecvCountType === ScanRecvCountType.Custom, qty);

    setCurrentItemRecvQty(qty);
  };

  const searchLotButton = () => {
    return (
      <Button
        disabled={!isLotReleased()}
        onClick={openSearchLotDrawer}
        type="text"
      >
        <SearchOutlined />
      </Button>
    );
  };

  const setLotCodeOnFormItem = (item: StringKAnyVPair, lotCode: string) => {
    item._receivingLot = lotCode;
    setCurrentLotCode(lotCode);
  };

  const submitItemButton = () => {
    return (
      <SmartButton
        className="mb-0"
        disabled={!isItemSubmittable(true, true, true)}
        onClick={receiveItem}
        size="large"
        type="primary"
      >
        {/*Submit*/}
        Save
      </SmartButton>
    );
  };

  // eslint-disable-next-line
  const submitRecvForm = () => {
    if (!props.state.enableUom) setRecvDirectlyVersion(recvDirectlyVersion + 1);
  };

  // eslint-disable-next-line
  const switchLocation = () => {
    //setCurrentLocation({});
    setCurrentStep(RecvStep.ScanLocation);
  };

  const toggleCurrentItemLockState = () => {
    setCurrentItemLocked(!currentItemLocked);
  };

  const toggleCurrentLotLockState = () => {
    setCurrentLotLocked(!currentLotLocked);
  };

  const tryNextStepByPatternCode = (code: string) => {
    const { dispatch, state } = props;
    const pl = extractPatternCodeList(state, code);
    const ln = getMatchedPatternCode(pl || [], PatternCodeType.Location);
    const po = getMatchedPatternCode(pl || [], PatternCodeType.PO);
    const sku = getMatchedPatternCode(pl || [], PatternCodeType.SKU);
    let ret = false;

    console.log('p', po, state.currentPo, state.currentLocation);
    if (po && po !== state.currentPo.poNumber) {
      receiveNextPOByPatternCode(code);
      ret = true;
    }

    //if (!ret && ln && ln !== currentLocation.locationCode) {
    if (!ret && ln) {
      if (ln === state.currentLocation.locationCode) {
        if (sku && isCurrentLocationSet()) {
          dispatch({ type: PRAction.SetMatchedPatternList, params: pl });
          setCurrentPatternLocationVersion(currentPatternLocationVersion + 1);
          setCurrentStep(RecvStep.ScanItem);
          ret = true;
        }
      } else {
        if (state.currentPo.poNum > 0) {
          dispatch({ type: PRAction.SetMatchedPatternList, params: pl });
          handleLocationChange(ln);
          ret = true;
        }
      }
    }

    if (!ret && sku && isCurrentLocationSet()) {
      if (state.currentPo.poNum > 0) {
        dispatch({ type: PRAction.SetMatchedPatternList, params: pl });
        //onScanItem(sku);
        setCurrentPatternLocationVersion(currentPatternLocationVersion + 1);
        ret = true;
      }
    }

    return ret;
  };

  const trySetFormByItemCode = (
    itemCode: string,
    curItem: StringKAnyVPair | undefined = undefined,
  ) => {
    const { dispatch, state } = props;
    //const item = findMatchItem2(itemCode, currentLocation.locationCode);
    const item = findMatchItem2(itemCode, state.currentLocation.locationCode);
    // console.log('i -->', item, itemCode);
    if (item) {
      if (isLotAvailableOnItem(state, item) && !currentLotLocked) {
        // lot was not set by outer logics
        if (!currentLotCode && item._receivingLot) {
          setCurrentLotCode(item._receivingLot);
        }
      }

      // can not use isUomEnable() here
      if (state.enableUom) {
        // whether need to add a lock
        if (isItemWithUom(state.uomInfo, defaultUom)) {
          //dispatch({ type: PRAction.SetUomInfo, params: { uom: item.uom, uomRate: item.uomRate } });
        }
      }
      // qty is not need to be the same as before
    }

    if (curItem) {
      if (state.enableUom) {
        const pcItem = findMatchProductCode(itemCode, curItem);
        //console.log('prd', pcItem);
        if (pcItem) {
          dispatch({ type: PRAction.SetUomInfo, params: { uom: pcItem.uom, uomRate: pcItem.uomRate } });
        }
      }
    }
  };

  const usePoQtyAsCurrentItemRecvQty = () => {
    const line = findMatchLine(currentItemCode);

    if (line) {
      const nQty = getMatchItemReceivingQty(props.state.recvItems, currentItemCode);
      const rQty = line.poQty - line.humanReceiveQty - nQty;
      //console.log('rqty', line, nQty, rQty);
      setCurrentItemRecvQty(rQty);
      setItemRecvInputVersion(itemRecvInputVersion + 1);
    }
  };

  const updateCurrentItemRecvQty = (
    qty: any,
    updateInput?: boolean,
  ) => {
    if (typeof qty === 'number') {
      const { state } = props;

      if (state.enablePOReceiveControl) {
        const line = findMatchLine(currentItemCode);
        const nQty = getMatchItemReceivingQty(props.state.recvItems, currentItemCode);
        const maxQty = Math.round(line.poQty * (1 + state.poOverRecvPercent));

        if (nQty + qty + line.humanReceiveQty > maxQty) {
          playSound('error');
          Modal.error({
            title: 'Error',
            content: `Cannot exceed total quatity[${maxQty}]`,
            onOk() {
              setCurrentItemRecvQty(currentItemRecvQty);
              setItemRecvInputVersion(itemRecvInputVersion + 1);
            },
          });
        } else {
          setCurrentItemRecvQty(qty);
        }
      } else {
        setCurrentItemRecvQty(qty);
      }
    }

    if (updateInput) setItemRecvInputVersion(itemRecvInputVersion + 1);
  };

  React.useEffect(() => {
    let itemReceived = false;

    if (!inited) {
      checkConfig();
      checkInitialRecvStep();
      setInited(true);
    }

    if (currentConfigVersion !== lastConfigVersion) {
      setDefaultItemRecvQty();
      setLastConfigVersion(currentConfigVersion);
    }

    if (scanRecvVersion !== lastScanRecvVersion) {
      console.log('sv', itemReceived, scanRecvVersion);
      receiveItem();
      itemReceived = true;
      setLastScanRecvVersion(scanRecvVersion);
      setTimeout(() => {
        // note: two jumps are needed for safety because of adding 1 in the clear function
        setItemScannerVersion(itemScannerVersion + 2);
      }, 500);
    }

    if (recvDirectlyVersion !== lastRecvDirectlyVersion) {
      console.log('dv', itemReceived, recvDirectlyVersion);
      if (!itemReceived) {
        receiveItem();
        itemReceived = true;
      }

      setLastRecvDirectlyVersion(recvDirectlyVersion);
    }

    if (currentPatternLocationVersion !== lastPatternLocationVersion) {
      checkPatternCode(PatternCodeType.SKU);
      setLastPatternLocationVersion(currentPatternLocationVersion);
    }
  }, [
    checkConfig,
    checkInitialRecvStep,
    checkPatternCode,
    currentConfigVersion,
    currentPatternLocationVersion,
    inited,
    itemScannerVersion,
    lastConfigVersion,
    lastRecvDirectlyVersion,
    lastPatternLocationVersion,
    lastScanRecvVersion,
    receiveItem,
    recvDirectlyVersion,
    scanRecvVersion,
    setDefaultItemRecvQty,
  ]);

  return (<>
    <SmartSpace className="ant-card ant-card-bordered mb-5">
      <div className="ant-card-body card-body-p10">
        <Row  align="middle" justify="space-between">
          <span>
            <Label className="tail-colon2" theme={{fontSize: '1.2em'}}>PO#</Label>
            <Label className="label-grey" theme={{fontSize: '1.2em'}}>{props.state.currentPo.poNumber || ''}</Label>
          </span>
          <SecondaryButton
            className="icon-btn-small"
            onClick={openSetScanQtyDialog}
          >
            <SettingOutlined />
          </SecondaryButton>
        </Row>
        {isCurrentLocationSet() && (
          <Row className="mt-10" align="middle" justify="space-between">
            <span>
              <Label className="tail-colon2" theme={{fontSize: '1.2em'}}>Location</Label>
              <Label className="label-grey" theme={{fontSize: '1.2em'}}>{props.state.currentLocation.locationCode}</Label>
            </span>
            {false && <SuccessButton
              className="icon-btn-small"
              disabled={!isLocationButtonEnable()}
              //onClick={switchLocation}
              onClick={openScanLocationDialog}
              size="small"
              type="primary"
            >
              <SelectOutlined />
            </SuccessButton>}
          </Row>
        )}
        {isItemScannable() && isItemScanned() && (
          <Row className="mt-10" align="middle" justify="space-between">
            <span>
              <Label className="tail-colon2" theme={{fontSize: '1.2em'}}>Item</Label>
              <Label className="text-bold2 label-grey" theme={{fontSize: '1.2em'}}>{currentItem.sku}</Label>
            </span>
          </Row>
        )}
      </div>
    </SmartSpace>
    <SmartSpace className="mb-5">
      {currentStep !== RecvStep.ReceivedItem && <SmartFormLabel
        style={{ color: GreenColor, fontWeight: 600, fontSize: 18 }}
      >
        {isLocationScannable() ? t("smart.poreceive.scanLocation") : 'Scan Item'}
      </SmartFormLabel>}
      {isLocationScannable() && (
        <SmartScanner
          className="mt-2 mb-5"
          //disabled={isFetching}
          isAutoFocus={focusOnLocation}
          placeholder="Input location here"
          onChangeValue={handleLocationChange}
        />
      )}
      {isItemScannable() && (
        <SmartScannerInput
          allowClear
          autoFocus
          className="fs-24 mt-2 mb-5 h-50"
          placeholder="Input item here"
          onClear={onClearScannedItem}
          onKeyDown={onItemScannerKeyDown}
          onScan={onScanItem}
          updateValue={itemScannerCode}
          version={itemScannerVersion}
        />
      )}
      {isLotScannable() && (<>
        <SmartFormLabel
          style={{ color: GreenColor, fontWeight: 600, fontSize: 18 }}
        >
          LOT#
        </SmartFormLabel>
        <Row className="mt-5" justify="space-between">
          <SmartScannerInput
            addonAfter={searchLotButton()}
            allowClear
            className="fs-24 h-50"
            defaultValue={currentLotCode}
            disabled={!isLotReleased()}
            onClear={onClearLotCode}
            onScan={onScanLot}
            placeholder="Input LOT# here"
            style={{width: 'calc(100% - 120px)'}}
            version={lotScannerVersion}
          />
          <SuccessButton
            className="fs-22 h-50 p-4-13"
            disabled={!isLotReleased()}
            onClick={openCreateLotDrawer}
          >
            <PlusOutlined />
          </SuccessButton>
          <Button
            className={currentLotLockClass()}
            onClick={toggleCurrentLotLockState}
          >
            {currentLotLocked ? <LockOutlined /> : <UnlockOutlined />}
          </Button>
        </Row>
      </>)}
      {isUomEnable() && (<>
        <Row align="middle" className="mt-15" justify="space-between">
          <Space>
            <SmartFormLabel
              style={{ color: GreenColor, fontWeight: 600, fontSize: 18 }}
            >
              UOM
            </SmartFormLabel>
            <span className="fs-18 text-grey">
              {`${props.state.uomInfo.uom}(${props.state.uomInfo.uomRate})`}
            </span>
          </Space>
          <Button
            className="fs-20 h-48 p-4-12 default-btn-info"
            disabled={!isUomEnable(true)}
            onClick={openUomSelector}
          >
            <SelectOutlined />
          </Button>
        </Row>
      </>)}
      {isItemSubmittable() && (<>
        <SmartFormLabel
          style={{ color: GreenColor, fontWeight: 600, fontSize: 18 }}
        >
          Qty
        </SmartFormLabel>
        <Row className="mt-5" justify="space-between">
          <SmartNumberInput
            className="num-input h-50"
            disabled={!isQtyChangeable()}
            key={`recv-input${itemRecvInputVersion}`}
            min={0}
            onChange={updateCurrentItemRecvQty}
            style={{width: 'calc(100% - 173px)'}}
            value={currentItemRecvQty}
          />
          <Button
            className="fs-16 h-50 p-4-8"
            disabled={!isQtyChangeable(true)}
            onClick={usePoQtyAsCurrentItemRecvQty}
          >
            Use PO Qty
          </Button>
          <Button
            className={currentItemLockClass()}
            onClick={toggleCurrentItemLockState}
          >
            {currentItemLocked ? <LockOutlined /> : <UnlockOutlined />}
          </Button>
        </Row>
        <SmartBlock className="mt-15">{submitItemButton()}</SmartBlock>
      </>)}
      {currentStep === RecvStep.ReceivedItem && (<>
        <Row className="mt-5">
          <Label theme={{fontSize: '1.3em'}}>Received Successfully!</Label>
        </Row>
        {receiveNextStepWizard()}
      </>)}
      {props.state.recvItems.length > 0 && (<>
        <Row className="mt-5">
          <Label theme={{fontSize: '1.3em'}}>{`${currentStep === RecvStep.ScanItem ? 'Receiving' : 'Received'}`} Lines</Label>
        </Row>
        {/*renderReceivingItems(props.state.recvItems)*/}
        <RecvItemList
          dispatch={props.dispatch}
          editable={currentStep === RecvStep.ScanItem}
          state={props.state}
        />
        {currentStep === RecvStep.ScanItem && confirmItemsButton()}
      </>)}
    </SmartSpace>
    {isPageRolledBack() && <SmartSpace>
      <SmartBackButton
        //onClick={() => history.goBack()}
        onClick={gotoPrev}
      />
    </SmartSpace>}
    {isRecvConfirmDrawerOpen && (
      <RecvConfirmDrawer
        dispatch={props.dispatch}
        onClose={closeConfirmReceivingItemsDrawer}
        onSave={saveReceivingItems}
        state={props.state}
        visible={isRecvConfirmDrawerOpen}
      />
    )}
    {isCreateLotDrawerOpen && (
      <CreateLotDrawer
        dispatch={props.dispatch}
        items={[currentItem]}
        onClose={closeCreateLotDrawer}
        onSave={onSelectLot}
        state={props.state}
        visible={isCreateLotDrawerOpen}
      />
    )}
    {isLocationDialogVisible && (
      <CodeInputDialog
        codeLabel="Location"
        maskClosable={false}
        onCancel={closeScanLocationDialog}
        onSave={onSwitchLocation}
        options={getDefaultLocation(props.state) ? [
          {label: 'Use as Default Location', value: 1, defaultChecked: true},
        ] : undefined}
        title="Change Location"
        visible={isLocationDialogVisible}
      />
    )}
    {isSearchLotDrawerOpen && (
      <SearchLotDrawer
        dispatch={props.dispatch}
        onClose={closeSearchLotDrawer}
        onSelect={onSelectLot}
        state={props.state}
        sku={currentItem.sku}
        visible={isSearchLotDrawerOpen}
      />
    )}
    {uomSelectorVisible && (
      <UomSelector
        bodyStyle={{padding: 10}}
        dispatch={props.dispatch}
        onCancel={closeUomSelector}
        onSelectUom={onSelectUom}
        refInfo={currentItem}
        state={props.state}
        title="Select UOM"
        visible={uomSelectorVisible}
      />
    )}
  </>);
};

export default RecvPo;
